import { type QueryClient } from '@tanstack/react-query';

import { resetRevokedDocumentSources } from '@/hooks/use-document-sources';
import { reportingKeys } from '@/hooks/use-reporting-query-keys';
import { TASK_QUERY_KEYS } from '@/hooks/use-tasks-helpers';
import { invalidateTaskShareSurfaces, taskShareKeys } from '@/hooks/use-task-shares';
import { commentsKeys } from '@/hooks/use-comments/keys';
import { taskAttachmentKeys } from '@/hooks/use-task-attachments';
import { taskLinkedKeys } from '@/hooks/use-task-linked-threads';

import { readShareRevokedAt, readShareString } from './share-payload-utils';

import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

/**
 * Recipient-side task share-access cache logic, shared by the app-root
 * ShareAccessRealtime provider. A task share NEVER confers correspondence content
 * access, so this only touches task caches. The task share-gated surfaces are
 * declared in `share-gated-cache-registry` (the same source `shouldDehydrateQuery`
 * reads) and a drift-guard test asserts this handler evicts every registry
 * prefix; the precise removal keys stay here because task surfaces are keyed
 * heterogeneously (org-scoped board, whole-namespace my-tasks feeds, taskId-scoped
 * children). See docs/plans/pub-893-task-sharing-redo.md.
 */

export interface TaskShareAccessEvent {
  readonly taskId: string | undefined;
  readonly organizationId: string | undefined;
}

const swallow = () => false;

export function readTaskShareEvent(
  payload: RealtimePostgresChangesPayload<Record<string, unknown>>,
): TaskShareAccessEvent {
  return {
    taskId: readShareString(payload.new, 'task_id') ?? readShareString(payload.old, 'task_id'),
    organizationId:
      readShareString(payload.new, 'organization_id') ??
      readShareString(payload.old, 'organization_id'),
  };
}

export function isTaskActiveAccessPayload(
  payload: RealtimePostgresChangesPayload<Record<string, unknown>>,
): boolean {
  if (payload.table !== 'task_shares') {
    return false;
  }
  // INSERT of an active row = grant; UPDATE that leaves revoked_at null =
  // permission change (the live View->Collaborate upgrade). Both refresh.
  if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
    return readShareRevokedAt(payload.new) === null;
  }
  return false;
}

export function isTaskRevocationPayload(
  payload: RealtimePostgresChangesPayload<Record<string, unknown>>,
): boolean {
  if (payload.table !== 'task_shares') {
    return false;
  }

  // Fire only on a real null-to-timestamp transition. `undefined` (REPLICA
  // IDENTITY skipped the column) is "unknown" and short-circuits; the migration
  // sets REPLICA IDENTITY FULL on task_shares so null is the pre-revoke state.
  if (payload.eventType === 'UPDATE') {
    return (
      readShareRevokedAt(payload.old) === null &&
      typeof readShareRevokedAt(payload.new) === 'string'
    );
  }

  if (payload.eventType === 'DELETE') {
    return readShareRevokedAt(payload.old) === null;
  }

  return false;
}

export function refreshTaskShareAccess(
  queryClient: QueryClient,
  event: TaskShareAccessEvent,
): void {
  const { organizationId, taskId } = event;
  if (!organizationId || !taskId) {
    return;
  }
  invalidateTaskShareSurfaces(queryClient, organizationId, taskId);
  // Refresh (not remove) the open task detail so a View->Collaborate upgrade
  // re-gates the edit affordances live; RLS re-decides on refetch.
  queryClient
    .invalidateQueries({ queryKey: [TASK_QUERY_KEYS.task, taskId, organizationId] })
    .catch(swallow);
}

export function evictRevokedTaskShare(queryClient: QueryClient, event: TaskShareAccessEvent): void {
  const { organizationId, taskId } = event;
  if (!organizationId || !taskId) {
    return;
  }
  // The reporting drill-down caches task titles it read under the recipient's
  // RLS. Its key carries the row ids, so it cannot be matched per-row — and the
  // cache is small and short-lived, so dropping the prefix is the cheap
  // correct move.
  // `resetQueries`, not `removeQueries`: removing the entry leaves a MOUNTED
  // observer still holding the map it already had, so an open drill-down keeps
  // rendering the title that was just revoked. Reset clears it AND refetches
  // the active observer, which re-derives the list from RLS.
  queryClient.resetQueries({ queryKey: reportingKeys.drilldownRowDetailAll() }).catch(() => {
    /* eviction is best-effort; the next read re-derives from RLS regardless */
  });
  // Remove (not invalidate) every cache that could still render the task the
  // recipient just lost — board, both my-tasks feeds, the open detail, its
  // subtasks, and the private-task CHILD surfaces that carry constituent PII
  // (comment bodies, attachment filenames, linked-thread subjects) — so the
  // tile/detail/panels vanish without a refocus and a persisted snapshot is
  // excluded from localStorage (see query-persistence.ts skip-list). Then
  // invalidate ONLY the (disjoint) share list. This mirrors
  // removeCorrespondenceAccessCaches: never invalidate a feed we are about to
  // remove, which would fire a refetch that removeQueries immediately cancels.
  // The my-tasks feeds are removed by their bare prefix because their keys lead
  // with userId, not organizationId ([my-tasks, userId] and
  // [my-tasks-by-org, userId, orgId]).
  queryClient.removeQueries({ queryKey: [TASK_QUERY_KEYS.tasks, organizationId] });
  queryClient.removeQueries({ queryKey: [TASK_QUERY_KEYS.myTasks] });
  queryClient.removeQueries({ queryKey: [TASK_QUERY_KEYS.myTasksByOrg] });
  queryClient.removeQueries({ queryKey: [TASK_QUERY_KEYS.task, taskId, organizationId] });
  queryClient.removeQueries({ queryKey: [TASK_QUERY_KEYS.subtasks, organizationId, taskId] });
  // Child surfaces holding PII (listPrefix / byTask are prefixes — they match
  // every filter variant of the task's comment list, attachments, linked threads).
  queryClient.removeQueries({ queryKey: commentsKeys.listPrefix(organizationId, 'task', taskId) });
  queryClient.removeQueries({ queryKey: taskAttachmentKeys.byTask(organizationId, taskId) });
  // The eviction PREFIX, not the per-viewer key: this must clear every
  // viewer's copy of the revoked task's threads, not just one.
  queryClient.removeQueries({ queryKey: taskLinkedKeys.threadsForTask(taskId) });
  // task-correspondence (summary + contact_name PII) — key shape from
  // use-task-correspondence.ts: ['task-correspondence', orgId, taskId].
  queryClient.removeQueries({ queryKey: ['task-correspondence', organizationId, taskId] });
  // email-tasks (['email-tasks', threadId, corrId, orgId, userId]) lists a
  // conversation's linked tasks, carrying the revoked private task's title. It is
  // keyed by threadId/corrId (not taskId), so it cannot be targeted per-task -
  // evict the whole namespace so the revoked task cannot linger in any
  // conversation's task list. Safe: rare revoke, refetch re-gates under RLS.
  queryClient.removeQueries({ queryKey: ['email-tasks'] });
  // A Pulse-written document's Sources section stores the task's title as a label.
  resetRevokedDocumentSources(queryClient, { kind: 'task', taskId }).catch(swallow);
  queryClient
    .invalidateQueries({ queryKey: taskShareKeys.shares(organizationId, taskId) })
    .catch(swallow);
}

// Reconnect catch-up. A grant / revoke that fired while the socket was down is
// lost — postgres_changes has no replay and the global query client does not
// refetch-on-reconnect — so refetch the recipient's own share lists and task
// feeds under current RLS: a revoked task drops out of the (fully re-fetched)
// lists and a new grant appears. Bounded on purpose: this does NOT evict PII
// from an already-open task detail on a missed revoke (a single-entity refetch
// can 404 and TanStack retains the stale row) — that definitive reconcile is the
// provider's session-start catch-up query keyed on a last-seen timestamp.
export function catchUpTaskShareAccess(queryClient: QueryClient): void {
  queryClient
    .invalidateQueries({ queryKey: [TASK_QUERY_KEYS.myTasks], refetchType: 'all' })
    .catch(swallow);
  queryClient
    .invalidateQueries({ queryKey: [TASK_QUERY_KEYS.myTasksByOrg], refetchType: 'all' })
    .catch(swallow);
  queryClient.invalidateQueries({ queryKey: taskShareKeys.all }).catch(swallow);
}
